import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Hash,
  Users,
  BellOff,
  Bell,
  Archive,
  ArchiveRestore,
  LogOut,
} from "lucide-react";
import { useTeamPresence } from "../../../contexts/TeamPresenceContext";
import {
  archiveConversation,
  muteConversation,
  leaveGroup,
} from "../../../../api/teamChat";

const MAX_VISIBLE_AVATARS = 5;

/**
 * TeamChatHeader — top-of-thread conversation summary + actions.
 *
 * Visual language matches ChatEntry's floating card:
 *   - `max-w-4xl px-6` outer column (left-aligned with thread)
 *   - `rounded-2xl bg-surface/80 backdrop-blur-md border` inner card
 *   - matching shadow tokens for light/dark
 *   - rounded-full action buttons sized like ChatEntry's controls
 *
 * Header sits at the top of the chat column, input sits at the bottom,
 * thread between — three surfaces in the same family.
 */

const TeamChatHeader = ({
  conversation,
  members = {},
  currentTeamId,
  onLeave,
}) => {
  const { onlineUsers, mutedConversations, updateMutedConversation } =
    useTeamPresence();

  const [archivedLocal, setArchivedLocal] = useState(false);
  const [leaving, setLeaving] = useState(false);

  // Listen for archive state changes from elsewhere (e.g. sidebar).
  // Bug fix: this was previously inside `useState(...)` instead of
  // `useEffect(...)`, so the listener was registered once on first
  // render and the cleanup return was discarded.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.team_id === currentTeamId) {
        setArchivedLocal(e.detail.archived);
      }
    };
    window.addEventListener("team:archive-changed", handler);
    return () => window.removeEventListener("team:archive-changed", handler);
  }, [currentTeamId]);

  const isMuted = mutedConversations.includes(currentTeamId);
  const convType = conversation?.type || "group";
  const convName =
    conversation?.name || conversation?.dm_display_name || "Conversation";

  const memberList = useMemo(() => {
    const list = Object.values(members);
    return list.sort((a, b) => {
      const aOnline = onlineUsers.has(a.uuid) ? 1 : 0;
      const bOnline = onlineUsers.has(b.uuid) ? 1 : 0;
      return bOnline - aOnline;
    });
  }, [members, onlineUsers]);

  const onlineCount = useMemo(
    () => memberList.filter((m) => onlineUsers.has(m.uuid)).length,
    [memberList, onlineUsers],
  );

  const visibleMembers = memberList.slice(0, MAX_VISIBLE_AVATARS);
  const overflowCount = memberList.length - MAX_VISIBLE_AVATARS;

  const handleMute = useCallback(async () => {
    await muteConversation(currentTeamId, !isMuted);
    updateMutedConversation(currentTeamId, !isMuted);
  }, [currentTeamId, isMuted, updateMutedConversation]);

  const handleArchive = useCallback(async () => {
    const newState = !archivedLocal;
    await archiveConversation(currentTeamId, newState);
    setArchivedLocal(newState);
    window.dispatchEvent(new CustomEvent("sidebar:refresh-team-conversations"));
  }, [currentTeamId, archivedLocal]);

  const handleLeave = useCallback(async () => {
    if (leaving) return;
    setLeaving(true);
    try {
      await leaveGroup(currentTeamId);
      window.dispatchEvent(
        new CustomEvent("sidebar:refresh-team-conversations"),
      );
      onLeave?.();
    } catch (e) {
      console.error("Failed to leave:", e);
    } finally {
      setLeaving(false);
    }
  }, [currentTeamId, leaving, onLeave]);

  if (!conversation) return null;

  const actionBtn =
    "h-8 w-8 inline-flex items-center justify-center rounded-full transition cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60";
  const actionBtnInactive =
    "text-mutedText hover:text-mainText hover:bg-mainText/[0.06]";

  return (
    <div className="w-full max-w-5xl px-6 pt-4 pb-2 shrink-0">
      <div
        className={[
          "relative w-full rounded-2xl bg-surface/80 backdrop-blur-md",
          "border border-surfaceBorder",
          "shadow-[0_4px_24px_rgba(0,0,0,0.08)]",
          "dark:shadow-[0_4px_24px_rgba(0,0,0,0.45)]",
          "px-4 py-2.5 flex items-center gap-x-3",
        ].join(" ")}
      >
        {/* Left: type indicator + title + meta */}
        <div className="flex items-center gap-x-2.5 min-w-0 flex-1">
          {convType === "dm" ? (
            (() => {
              const dmName = conversation.dm_display_name || "Unknown";
              const initial = (dmName[0] || "?").toUpperCase();
              const dmUuid = conversation.dm_user_uuid;
              const isOnline = onlineUsers.has(dmUuid);
              return (
                <span className="relative shrink-0">
                  <span className="w-7 h-7 rounded-full bg-velBlue/15 flex items-center justify-center text-[12px] font-normal text-velBlue">
                    {initial}
                  </span>
                  {isOnline && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-activeCase rounded-full border-2 border-mainBg" />
                  )}
                </span>
              );
            })()
          ) : (
            <div
              className={[
                "w-7 h-7 rounded-lg flex items-center justify-center shrink-0",
                convType === "channel" ? "bg-activeCase/15" : "bg-velBlue/15",
              ].join(" ")}
            >
              {convType === "channel" ? (
                <Hash className="h-3.5 w-3.5 text-activeCase" strokeWidth={2} />
              ) : (
                <Users className="h-3.5 w-3.5 text-velBlue" strokeWidth={2} />
              )}
            </div>
          )}

          <div className="min-w-0">
            <h2 className="text-[13px] font-normal tracking-wide text-mainText truncate leading-tight">
              {convName}
            </h2>
            <p className="text-[11px] font-light tracking-wide text-mutedText/80 leading-tight mt-0.5">
              {memberList.length} member{memberList.length !== 1 ? "s" : ""}
              {onlineCount > 0 && (
                <span className="text-activeCase ml-1">
                  · {onlineCount} online
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Center: member avatars (channel/group only) */}
        {convType !== "dm" && memberList.length > 0 && (
          <div className="flex -space-x-1.5 shrink-0">
            {visibleMembers.map((member) => {
              const name = member.display_name || member.first_name || "?";
              const initial = (name[0] || "?").toUpperCase();
              const isOnline = onlineUsers.has(member.uuid);
              return (
                <div
                  key={member.uuid}
                  className="relative"
                  title={`${name}${isOnline ? " (online)" : ""}`}
                >
                  <div
                    className={[
                      "w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-normal text-white border-2 border-surface",
                      isOnline ? "bg-velBlue" : "bg-mutedText/40",
                    ].join(" ")}
                  >
                    {initial}
                  </div>
                </div>
              );
            })}
            {overflowCount > 0 && (
              <div className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-normal text-mutedText bg-surface border-2 border-surface">
                +{overflowCount}
              </div>
            )}
          </div>
        )}

        {/* Right: action buttons */}
        <div className="flex items-center gap-x-0.5 shrink-0">
          <button
            onClick={handleMute}
            title={isMuted ? "Unmute" : "Mute"}
            className={[
              actionBtn,
              isMuted
                ? "text-sunColor bg-sunColor/[0.1] hover:bg-sunColor/[0.18]"
                : actionBtnInactive,
            ].join(" ")}
          >
            {isMuted ? (
              <BellOff className="h-3.5 w-3.5" strokeWidth={2} />
            ) : (
              <Bell className="h-3.5 w-3.5" strokeWidth={2} />
            )}
          </button>

          <button
            onClick={handleArchive}
            title={archivedLocal ? "Unarchive" : "Archive"}
            className={[
              actionBtn,
              archivedLocal
                ? "text-velBlue bg-velBlue/[0.1] hover:bg-velBlue/[0.18]"
                : actionBtnInactive,
            ].join(" ")}
          >
            {archivedLocal ? (
              <ArchiveRestore className="h-3.5 w-3.5" strokeWidth={2} />
            ) : (
              <Archive className="h-3.5 w-3.5" strokeWidth={2} />
            )}
          </button>

          {convType === "group" && (
            <button
              onClick={handleLeave}
              disabled={leaving}
              title="Leave group"
              className={[
                actionBtn,
                "text-mutedText hover:text-logoutColor hover:bg-logoutColor/[0.1] disabled:opacity-50",
              ].join(" ")}
            >
              <LogOut className="h-3.5 w-3.5" strokeWidth={2} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamChatHeader;
